import React, { useState, useEffect } from 'react';
import './TradingForm.css';
import { QuoteData, TRADING_SYMBOL } from '../services/MetaCopierService';

// LocalStorage keys for trading parameters
const STORAGE_KEYS = {
  LOTS: 'tradingapp_lot_size',
  TP: 'tradingapp_take_profit',
  SL: 'tradingapp_stop_loss'
};

interface TradingFormProps {
  onTrade: (action: 'BUY' | 'SELL', lots: number, tp: number, sl: number) => void;
  isConnected: boolean;
  quote: QuoteData | null;
}

const TradingForm: React.FC<TradingFormProps> = ({ onTrade, isConnected, quote }) => {
  // Load from localStorage or use defaults
  const [lots, setLots] = useState<string>(() => {
    return localStorage.getItem(STORAGE_KEYS.LOTS) || '0.01';
  });
  const [tp, setTp] = useState<string>(() => {
    return localStorage.getItem(STORAGE_KEYS.TP) || '1000';
  });
  const [sl, setSl] = useState<string>(() => {
    return localStorage.getItem(STORAGE_KEYS.SL) || '1000';
  });
  const [isProcessing, setIsProcessing] = useState(false);

  // Save lots to localStorage when it changes
  useEffect(() => {
    if (lots) {
      localStorage.setItem(STORAGE_KEYS.LOTS, lots);
    }
  }, [lots]);

  // Save TP to localStorage when it changes
  useEffect(() => {
    if (tp) {
      localStorage.setItem(STORAGE_KEYS.TP, tp);
    }
  }, [tp]);

  // Save SL to localStorage when it changes
  useEffect(() => {
    if (sl) {
      localStorage.setItem(STORAGE_KEYS.SL, sl);
    }
  }, [sl]);

  const handleTrade = async (action: 'BUY' | 'SELL') => {
    if (!isConnected) {
      alert('Please connect with your API key first');
      return;
    }

    const lotsNum = parseFloat(lots);
    const tpNum = tp ? parseFloat(tp) : 0;
    const slNum = sl ? parseFloat(sl) : 0;

    if (isNaN(lotsNum) || lotsNum <= 0) {
      alert('Please enter a valid lot size');
      return;
    }

    setIsProcessing(true);
    await onTrade(action, lotsNum, tpNum, slNum);
    setIsProcessing(false);
  };

  return (
    <div className="trading-form-container">
      <div className="glass-card trading-card">
        <div className="symbol-header">
          <div className="symbol-icon">
            <span className="gold-icon">⚜</span>
          </div>
          <div className="symbol-info">
            <h2 className="symbol-name">{TRADING_SYMBOL}</h2>
            <p className="symbol-description">Gold vs US Dollar</p>
            {quote && (
              <div className="quote-display">
                <span className="quote-label">Bid:</span>
                <span className="quote-value bid">{quote.bid.toFixed(2)}</span>
                <span className="quote-separator">|</span>
                <span className="quote-label">Ask:</span>
                <span className="quote-value ask">{quote.ask.toFixed(2)}</span>
              </div>
            )}
          </div>
        </div>

        <div className="trading-inputs">
          <div className="input-group">
            <label htmlFor="lots">
              <span className="label-icon">📊</span>
              Lot Size
            </label>
            <input
              type="number"
              id="lots"
              value={lots}
              onChange={(e) => setLots(e.target.value)}
              step="0.01"
              min="0.01"
              className="glass-input"
              placeholder="0.01"
            />
            <span className="input-hint">Standard lots</span>
          </div>

          <div className="input-row">
            <div className="input-group">
              <label htmlFor="tp">
                <span className="label-icon">🎯</span>
                Take Profit (USD)
              </label>
              <input
                type="number"
                id="tp"
                value={tp}
                onChange={(e) => setTp(e.target.value)}
                step="1"
                min="0"
                className="glass-input"
                placeholder="1500 (Optional)"
              />
              <span className="input-hint">Target profit in USD (optional)</span>
            </div>

            <div className="input-group">
              <label htmlFor="sl">
                <span className="label-icon">🛡️</span>
                Stop Loss (USD)
              </label>
              <input
                type="number"
                id="sl"
                value={sl}
                onChange={(e) => setSl(e.target.value)}
                step="1"
                min="0"
                className="glass-input"
                placeholder="500 (Optional)"
              />
              <span className="input-hint">Maximum loss in USD (optional)</span>
            </div>
          </div>
        </div>

        <div className="trade-buttons">
          <button
            onClick={() => handleTrade('BUY')}
            disabled={!isConnected || isProcessing}
            className="glass-button buy-button"
          >
            <span className="button-text">
              {isProcessing ? '⏳ Processing...' : '📈 BUY'}
            </span>
            <div className="button-glow"></div>
          </button>

          <button
            onClick={() => handleTrade('SELL')}
            disabled={!isConnected || isProcessing}
            className="glass-button sell-button"
          >
            <span className="button-text">
              {isProcessing ? '⏳ Processing...' : '📉 SELL'}
            </span>
            <div className="button-glow"></div>
          </button>
        </div>

        {!isConnected && (
          <div className="warning-banner">
            <span className="warning-icon">⚠️</span>
            <span>Please connect with your API key to start trading</span>
          </div>
        )}
      </div>
    </div>
  );
};

export default TradingForm;
